import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Truck, ChevronLeft, ChevronRight, Plus, Edit2, Trash2, User, Briefcase, Info, ChevronUp, ChevronDown,
} from 'lucide-react';
import { api, extractError } from '../lib/api';
import { fmtDate } from '../lib/date';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../lib/auth';
import { useConfirm, ConfirmButton } from '../lib/confirm';
import { DeviceAssignDialog, DeviceAssignmentChip } from '../components/DeviceAssignDialog';

const WEEKS_TO_SHOW = 4;

/**
 * DevicePlantafelPage – Wochenweise Plantafel für Geräte.
 *
 * Layout:
 *   Zeile pro Gerät × Spalte pro Wochentag (Mo–Sa).
 *   Jeder Zellen-Slot zeigt: Baustelle + Fahrer + Stunden.
 *   Klick auf Zelle → Assign-Dialog (Baustelle + Fahrer + Stunden wählen).
 *
 * Zusätzlich:
 *   - "Fahrer-Pool" rechts: alle MA mit context=device + solche mit Absence 'Ersatz Geräte' heute
 *   - Warnung: Gerät ohne Fahrer
 */
export default function DevicePlantafelPage() {
  const { user, can } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEditDev = can('device_plantafel', 'edit') || can('device_plantafel', 'create');
  const canDeleteDev = can('device_plantafel', 'delete');

  const [today] = useState(new Date());
  const [anchorDate, setAnchorDate] = useState(startOfWeek(today, { weekStartsOn: 1 }));
  const [devices, setDevices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [dayProjectOrder, setDayProjectOrder] = useState([]);
  const [overHoursDeviceIds, setOverHoursDeviceIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState({ open: false, assignment: null, deviceId: '', date: '' });

  const days = useMemo(() => {
    const list = [];
    for (let w = 0; w < WEEKS_TO_SHOW; w++) {
      for (let d = 0; d < 6; d++) {
        list.push(addDays(anchorDate, w * 7 + d));
      }
    }
    return list;
  }, [anchorDate]);

  const fromIso = format(days[0], 'yyyy-MM-dd');
  const toIso = format(days[days.length - 1], 'yyyy-MM-dd');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dv, em, pr, ass, abs, dpo, wn] = await Promise.all([
        api.get('/devices'),
        api.get('/employees'),
        api.get('/projects'),
        api.get('/device-assignments', { params: { from_date: fromIso, to_date: toIso } }),
        api.get('/absences', { params: { from_date: fromIso, to_date: toIso } }),
        api.get('/day-project-order', { params: { from_date: fromIso, to_date: toIso } }),
        api.get('/warnings', { params: { from_date: fromIso, to_date: toIso } }),
      ]);
      setDevices(dv.data);
      setEmployees(em.data);
      setProjects(pr.data);
      setAssignments(ass.data);
      setAbsences(abs.data);
      setDayProjectOrder(dpo.data || []);
      const ohSet = new Set();
      for (const o of (wn.data?.over_hours || [])) for (const id of (o.device_assignment_ids || [])) ohSet.add(id);
      setOverHoursDeviceIds(ohSet);
    } catch (err) {
      toast.error(extractError(err));
    } finally { setLoading(false); }
  }, [fromIso, toIso]);
  useEffect(() => { load(); }, [load]);

  const empById = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);
  const projById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const activeDevices = useMemo(() => devices.filter(d => d.active), [devices]);
  const deviceEmployees = useMemo(() => employees.filter(e => e.active && e.assignment_context === 'device'), [employees]);

  // Absencen "Ersatz Geräte" heute erkennen → diese MA erscheinen zusätzlich in Palette
  const todayIso = format(today, 'yyyy-MM-dd');
  const substituteDrivers = useMemo(() => {
    const ids = new Set();
    for (const ab of absences) {
      if (ab.type === 'Ersatz Geräte' && ab.date === todayIso) ids.add(ab.employee_id);
    }
    return employees.filter(e => e.active && ids.has(e.id));
  }, [absences, employees, todayIso]);

  const dayOrderMap = useMemo(() => {
    const m = {};
    for (const o of dayProjectOrder) m[`${o.date}::${o.project_id}`] = o.sort_order;
    return m;
  }, [dayProjectOrder]);

  // Rang eines Projekts an einem Tag (gemeinsam mit der normalen Plantafel)
  const projRankOnDay = useCallback((date, pid) => {
    if (!pid) return 99999;
    const dv = dayOrderMap[`${date}::${pid}`];
    if (dv !== undefined) return dv;
    return 1000 + (projById[pid]?.sort_order ?? 0);
  }, [dayOrderMap, projById]);

  const assignmentsByDeviceDay = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      const k = `${a.device_id}::${a.date}`;
      if (!m[k]) m[k] = [];
      m[k].push(a);
    }
    // Stopps nach gemeinsamer Tages-Projektreihenfolge sortieren (synchron mit normaler Plantafel)
    for (const k in m) m[k].sort((x, y) => {
      const rx = projRankOnDay(x.date, x.project_id);
      const ry = projRankOnDay(y.date, y.project_id);
      if (rx !== ry) return rx - ry;
      return (x.sort_order ?? 0) - (y.sort_order ?? 0);
    });
    return m;
  }, [assignments, projRankOnDay]);

  const reorder = async (list, fromIdx, dir) => {
    const toIdx = fromIdx + dir;
    if (toIdx < 0 || toIdx >= list.length) return;
    const cur = list[fromIdx];
    const neighbor = list[toIdx];
    // Unterschiedliche Projekte -> gemeinsame Tages-Projektreihenfolge ändern (wirkt auch in der normalen Plantafel)
    if (cur.project_id && neighbor.project_id && cur.project_id !== neighbor.project_id) {
      const date = cur.date;
      // aktuelle Projektreihenfolge dieses Tages ermitteln (alle Projekte, die an dem Tag Geräte-Stopps haben)
      const dayProjIds = Array.from(new Set(
        assignments.filter(a => a.date === date && a.project_id).map(a => a.project_id)
      ));
      dayProjIds.sort((p, q) => projRankOnDay(date, p) - projRankOnDay(date, q));
      const from = dayProjIds.indexOf(cur.project_id);
      const target = dayProjIds.indexOf(neighbor.project_id);
      if (from < 0 || target < 0) return;
      dayProjIds.splice(from, 1);
      const newTarget = dayProjIds.indexOf(neighbor.project_id);
      const insertIdx = dir > 0 ? newTarget + 1 : newTarget;
      dayProjIds.splice(insertIdx, 0, cur.project_id);
      try {
        await api.post('/day-project-order/reorder', { date, ordered_project_ids: dayProjIds });
        toast.success('Reihenfolge für diesen Tag angepasst (auch in der Plantafel).');
        await load();
      } catch (err) { toast.error(extractError(err)); }
      return;
    }
    // Gleiches/kein Projekt -> nur lokale Geräte-Route anpassen
    const ids = list.map(a => a.id);
    [ids[fromIdx], ids[toIdx]] = [ids[toIdx], ids[fromIdx]];
    try {
      await api.post('/device-assignments/reorder', { ordered_ids: ids });
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const openAssignDialog = (device, dateIso, existing = null) => {
    const iso = typeof dateIso === 'string' ? dateIso : format(dateIso, 'yyyy-MM-dd');
    setAssignDialog({
      open: true,
      assignment: existing || null,
      deviceId: device.id,
      date: iso,
      defaultDriverId: existing ? existing.driver_employee_id : device.default_driver_employee_id,
      defaultHours: existing ? existing.hours : 8,
      defaultProjectId: existing ? existing.project_id : null,
      defaultNotes: existing ? existing.notes : '',
    });
  };

  const goPrev = () => setAnchorDate(addDays(anchorDate, -7));
  const goNext = () => setAnchorDate(addDays(anchorDate, 7));
  const goToday = () => setAnchorDate(startOfWeek(new Date(), { weekStartsOn: 1 }));

  return (
    <div className="flex h-screen">
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-white">
        {/* Toolbar */}
        <div className="h-11 flex items-center px-3 border-b border-slate-200 gap-2 flex-shrink-0 flex-wrap">
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={goPrev} className="h-7 w-7 p-0"><ChevronLeft size={14}/></Button>
            <Button size="sm" variant="outline" onClick={goToday} className="h-7 text-xs">Heute</Button>
            <Button size="sm" variant="outline" onClick={goNext} className="h-7 w-7 p-0"><ChevronRight size={14}/></Button>
          </div>
          <div className="text-sm font-mono">
            <strong>Geräte-Plantafel:</strong> {fmtDate(fromIso)} – {fmtDate(toIso)}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Badge className="bg-orange-100 text-orange-900 hover:bg-orange-100 border-orange-200 flex items-center gap-1">
              <Truck size={11}/>{activeDevices.length} Geräte
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1">
              <User size={11}/>{deviceEmployees.length} Geräte-MA
              {substituteDrivers.length > 0 && <span className="text-emerald-700 font-bold ml-1">+{substituteDrivers.length}</span>}
            </Badge>
          </div>
        </div>

        {/* Board */}
        <div className="flex-1 overflow-auto scroll-thin">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Lädt Geräte-Plantafel…</div>
          ) : activeDevices.length === 0 ? (
            <div className="p-8 text-center">
              <Truck size={40} className="mx-auto text-slate-300 mb-2"/>
              <div className="text-sm text-slate-600 mb-2">Noch keine aktiven Geräte.</div>
              <Button size="sm" onClick={() => window.location.href = '/geraete'}>
                Zur Geräteverwaltung
              </Button>
            </div>
          ) : (
            <table className="w-full border-collapse" data-testid="device-plantafel-table">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-slate-200">
                  <th className="p-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider bg-slate-50 sticky left-0" style={{ minWidth: 180 }}>Gerät</th>
                  {days.map((d, idx) => {
                    const isSat = d.getDay() === 6;
                    const isToday = format(d, 'yyyy-MM-dd') === todayIso;
                    return (
                      <th key={idx} className={`p-1 text-xs text-slate-600 border-l border-slate-200 ${isSat ? 'bg-slate-50' : ''} ${isToday ? 'bg-blue-50 text-blue-900 font-bold' : ''}`}>
                        <div className="font-semibold">{format(d, 'EEE', { locale: de })}</div>
                        <div className="font-mono text-[10px]">{format(d, 'dd.MM.')}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {activeDevices.map(dev => {
                  const defaultDriver = dev.default_driver_employee_id ? empById[dev.default_driver_employee_id] : null;
                  return (
                    <tr key={dev.id} className="border-b border-slate-200 hover:bg-slate-50/50">
                      <td className="p-2 sticky left-0 bg-white border-r border-slate-200" style={{ minWidth: 180 }}>
                        <div className="flex items-start gap-2">
                          <span className="inline-block w-3 h-3 rounded-sm mt-1" style={{ background: dev.color || '#64748b' }}/>
                          <div>
                            <div className="font-semibold text-sm">{dev.name}</div>
                            <div className="text-[10px] text-slate-500">
                              {dev.device_type}
                              {dev.inventory_number && ` · ${dev.inventory_number}`}
                            </div>
                            {defaultDriver && (
                              <div className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-1">
                                <User size={9}/>{defaultDriver.first_name} {defaultDriver.last_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {days.map((d, idx) => {
                        const iso = format(d, 'yyyy-MM-dd');
                        const list = assignmentsByDeviceDay[`${dev.id}::${iso}`] || [];
                        const isToday = iso === todayIso;
                        const isSat = d.getDay() === 6;
                        return (
                          <td
                            key={idx}
                            className={`p-1 border-l border-slate-200 align-top ${isSat ? 'bg-slate-50' : ''} ${isToday ? 'bg-blue-50/30' : ''}`}
                            data-testid={`device-cell-${dev.id}-${iso}`}
                          >
                            <div className="space-y-1">
                              {list.map((a, aIdx) => (
                                <div key={a.id} className="flex items-stretch gap-0.5">
                                  {canEditDev && list.length > 1 && (
                                    <div className="flex flex-col justify-center">
                                      <button onClick={(e) => { e.stopPropagation(); reorder(list, aIdx, -1); }} disabled={aIdx === 0} className="text-slate-400 hover:text-slate-800 disabled:opacity-30 leading-none" title="nach oben" data-testid={`device-up-${a.id}`}><ChevronUp size={11}/></button>
                                      <button onClick={(e) => { e.stopPropagation(); reorder(list, aIdx, 1); }} disabled={aIdx === list.length - 1} className="text-slate-400 hover:text-slate-800 disabled:opacity-30 leading-none" title="nach unten" data-testid={`device-down-${a.id}`}><ChevronDown size={11}/></button>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <DeviceAssignmentChip
                                      assignment={a}
                                      device={dev}
                                      project={a.project_id ? projById[a.project_id] : null}
                                      driver={a.driver_employee_id ? empById[a.driver_employee_id] : null}
                                      defaultDriver={defaultDriver}
                                      onClick={canEditDev ? () => openAssignDialog(dev, iso, a) : undefined}
                                      warn={overHoursDeviceIds.has(a.id)}
                                    />
                                  </div>
                                </div>
                              ))}
                              {canEditDev && (
                                <button
                                  onClick={() => openAssignDialog(dev, iso)}
                                  className="w-full text-[10px] text-slate-400 hover:text-slate-800 hover:bg-blue-50/40 rounded border border-dashed border-slate-200 py-0.5 flex items-center justify-center gap-1"
                                  data-testid={`device-add-${dev.id}-${iso}`}
                                  title="Geräte-Einsatz hinzufügen"
                                >
                                  <Plus size={10}/>{list.length === 0 ? 'Einsatz' : ''}
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Right sidebar: Fahrer-Pool */}
      <div className="w-56 border-l border-slate-200 bg-slate-50 flex-shrink-0 hidden md:flex flex-col">
        <div className="h-10 border-b border-slate-200 flex items-center px-3">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
            <User size={12}/>Fahrer-Pool
          </div>
        </div>
        <div className="p-2 flex-1 overflow-y-auto scroll-thin space-y-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 px-1">
              Geräte-Team ({deviceEmployees.length})
            </div>
            {deviceEmployees.length === 0 ? (
              <div className="text-xs text-slate-400 italic px-1">Kein MA im Geräte-Kontext.</div>
            ) : (
              <div className="space-y-1">
                {deviceEmployees.map(e => (
                  <div key={e.id} className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs flex items-center gap-1.5" data-testid={`pool-driver-${e.id}`}>
                    <User size={10} className="text-orange-600"/>
                    <span className="font-medium">{e.first_name} {e.last_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {substituteDrivers.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1.5 px-1">
                Ersatz-Fahrer heute ({substituteDrivers.length})
              </div>
              <div className="space-y-1">
                {substituteDrivers.map(e => (
                  <div key={e.id} className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-xs flex items-center gap-1.5">
                    <User size={10} className="text-emerald-700"/>
                    <span className="font-medium">{e.first_name} {e.last_name}</span>
                    <Badge className="bg-emerald-200 text-emerald-900 hover:bg-emerald-200 text-[9px] ml-auto">Ersatz</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10px] text-slate-500 pt-2 border-t border-slate-200 flex items-start gap-1">
            <Info size={10} className="mt-0.5 flex-shrink-0"/>
            <span>Klick auf eine Zelle, um einen Geräte-Einsatz anzulegen oder zu bearbeiten.</span>
          </div>
        </div>
      </div>

      <DeviceAssignDialog
        state={assignDialog}
        onClose={() => setAssignDialog({ ...assignDialog, open: false })}
        employees={employees}
        projects={projects}
        absences={absences}
        onSaved={load}
        canEdit={canEditDev}
        canDelete={canDeleteDev}
      />
    </div>
  );
}
